import {
  FormContext,
  FormInputGroupLayoutContext,
  FormState,
  optimizedRetryOption,
  Select,
  SelectProps,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { NormalError } from "savant-connector";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { AdbsCharacterSetCollection, AdbsCharacterSetSummary } from "../../gen/clients/mchub-azure-api-client-adb";
import { IdResourceType } from "../../helpers/idHelper";
import { CharacterSetType, responseItemstoArray } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getOciRegion } from "../../utils";

  type BasicProps = Omit<SelectProps, "options">
export interface Props extends BasicProps {
    subscriptionId: string;
    location: string;
    characterSetType: CharacterSetType;
    onError?: (error: NormalError) => void;
    onMissingDependencies?: (missingDependencies: IdResourceType[]) => void;
  }
  
export const AdbsDbCharacterSetSelect = (
  {
    defaultValue,
    fieldName,
    label,
    placeholder,
    required,
    subField,
    tooltip,
    validator,
    subscriptionId,
    location,
    onError,
    onMissingDependencies,
    testId,
    characterSetType,
  }: Props,
): JSX.Element => {
  const [key, setKey] = React.useState<string>(uniqueGUID());
  
  const { groupName } = React.useContext(
    FormInputGroupLayoutContext,
  );
  
  const form: FormState = React.useContext(FormContext);
  
  const { loading, response, error } = useQueryCall({
    wait: !(subscriptionId && location),
    method: apiClients.withRegion(getOciRegion(location)).adbsDatabaseApi.listAdbsDbCharacterSets,
    options: {
      args: { subscriptionId, characterSetType },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: characterSetType === CharacterSetType.DATABASE
          ? Messages.notifications.failure.messages.loadDbCharacterSet()
          : Messages.notifications.failure.messages.loadDbNationalCharacterSet(),
      },
    },
  });
  const characterSets = (response
      && responseItemstoArray<AdbsCharacterSetCollection, AdbsCharacterSetSummary>(response)) || [];
  const options = characterSets?.map(charSet => ({
    id: charSet.name || "",
    text: charSet.name || "",
  }));
  
  React.useEffect(() => {
    form.setValue(undefined, fieldName, groupName);
    setKey(uniqueGUID());
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionId, location]);
  
  React.useEffect(() => {
    setKey(uniqueGUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(characterSets)]);
  
  React.useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  
  const internalOnClick = (): void => {
    const missingDependencies: IdResourceType[] = [];
    if (!subscriptionId) missingDependencies.push(IdResourceType.SUBSCRIPTIONS);
    if (!location) missingDependencies.push(IdResourceType.LOCATION);
    if (missingDependencies.length > 0) {
      onMissingDependencies?.(missingDependencies);
    }
  };
  
  const derivedPlaceholder = !loading && !error && options.length === 0
    ? Messages.createCommon.loadingNone(label?.toLowerCase() || "")
    : placeholder;
  
  return (
    <Select
      key={key}
      testId={testId}
      loading={!!subscriptionId && !!location && loading}
      required={required}
      subField={subField}
      fieldName={fieldName}
      label={label}
      options={options}
      placeholder={derivedPlaceholder}
      defaultValue={defaultValue || (options[0] && [options[0].text])}
      tooltip={tooltip}
      onClick={internalOnClick}
      validator={validator}
    />
  );
};
